import { useQueryClient } from '@tanstack/react-query';
import {
  Button,
  cn,
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Pagination,
  RiCheckLine,
  Spinner,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  toast,
} from 'component-library';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { usePostPlotPortfolioSubscription } from '@/api/rest/resources/plotPortfolioSubscriptions';
import { PlotPortfolioProduct } from '@/api/rest/resources/types/plotPortfolioProducts';
import { Form } from '@/components';
import { SuspenseBoundary } from '@/components/SuspenseBoundary';
import { PLOT_PORTFOLIO_PRODUCTS_QUERY_KEY, usePlotPortfolioProducts } from '@/hooks/usePlotPortfolioProducts';
import { PLOT_PORTFOLIO_SUBSCRIPTIONS_QUERY_KEY } from '@/hooks/usePlotPortfolioSubscriptions';

import {
  PlotPortfolioSubscriptionFormFields,
  PlotPortfolioSubscriptionFormValues,
} from './PlotPortfolioSubscriptionFormFields';

const PRODUCT_PAGE_SIZE = 5;

type AddPlotPortfolioSubscriptionDialogProps = {
  open: boolean;
  onClose: () => void;
  organizationId: string;
};

export const AddPlotPortfolioSubscriptionDialog = ({
  open,
  onClose,
  organizationId,
}: AddPlotPortfolioSubscriptionDialogProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onOpenChange={(isOpen) => !isOpen && onClose()}>
      <DialogContent className='min-w-[45vw]'>
        <DialogHeader>
          <DialogTitle>{t('shared.ndiOrganizationSubscriptions.plotPortfolioDialog.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          {open && (
            <SuspenseBoundary fallback={<Spinner size={10} />}>
              <AddPlotPortfolioSubscriptionFormContent organizationId={organizationId} onClose={onClose} />
            </SuspenseBoundary>
          )}
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
};

const AddPlotPortfolioSubscriptionFormContent = ({
  organizationId,
  onClose,
}: {
  organizationId: string;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const [selectedProduct, setSelectedProduct] = useState<PlotPortfolioProduct | undefined>(undefined);

  return (
    <Stack spacing={4}>
      <Stack spacing={2}>
        <span className='typography-body2 font-medium'>{t('shared.ndiOrganizationSubscriptions.dialog.product')}</span>
        <SuspenseBoundary fallback={<Spinner size={10} />}>
          <PlotPortfolioProductTable selectedId={selectedProduct?.id} onSelect={setSelectedProduct} />
        </SuspenseBoundary>
      </Stack>
      {selectedProduct && (
        <PlotPortfolioSubscriptionOverrideForm
          key={selectedProduct.id}
          organizationId={organizationId}
          plotPortfolioProduct={selectedProduct}
          onClose={onClose}
        />
      )}
    </Stack>
  );
};

const PlotPortfolioProductTable = ({
  selectedId,
  onSelect,
}: {
  selectedId?: string;
  onSelect: (plotPortfolioProduct: PlotPortfolioProduct) => void;
}) => {
  const { t } = useTranslation();
  const [currentPage, setCurrentPage] = useState(1);
  const { data } = usePlotPortfolioProducts({
    limit: PRODUCT_PAGE_SIZE,
    offset: (currentPage - 1) * PRODUCT_PAGE_SIZE,
  });
  const plotPortfolioProducts = data.results;

  return (
    <Stack spacing={3}>
      <div className='rounded-2xl bg-white-100 border border-neutral-black-12'>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className='w-px' />
              <TableHead className='typography-overline h-12 px-4 py-3 text-left align-middle leading-4 text-text-secondary'>
                {t('shared.ndiOrganizationSubscriptions.fields.displayName')}
              </TableHead>
              <TableHead className='typography-overline h-12 px-4 py-3 text-left align-middle leading-4 text-text-secondary'>
                {t('shared.ndiProducts.fields.initialSetupCostCredits')}
              </TableHead>
              <TableHead className='typography-overline h-12 px-4 py-3 text-left align-middle leading-4 text-text-secondary'>
                {t('shared.ndiProducts.fields.plotCreationCostCredits')}
              </TableHead>
              <TableHead className='typography-overline h-12 px-4 py-3 text-left align-middle leading-4 text-text-secondary'>
                {t('shared.ndiProducts.fields.maxPlots')}
              </TableHead>
              <TableHead className='typography-overline h-12 px-4 py-3 text-left align-middle leading-4 text-text-secondary'>
                {t('shared.ndiProducts.fields.maxHectares')}
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {plotPortfolioProducts.length === 0 && (
              <TableRow>
                <TableCell colSpan={6} className='typography-body1 p-4 text-center text-text-secondary'>
                  {t('shared.ndiProducts.emptyState')}
                </TableCell>
              </TableRow>
            )}
            {plotPortfolioProducts.map((plotPortfolioProduct) => (
              <TableRow
                key={plotPortfolioProduct.id}
                data-testid='plot-portfolio-subscription-product-row'
                className={cn(
                  'border-b border-divider cursor-pointer hover:bg-neutral-black-4',
                  plotPortfolioProduct.id === selectedId && 'bg-neutral-hover',
                )}
                onClick={() => onSelect(plotPortfolioProduct)}
              >
                <TableCell className='w-px pl-4'>
                  {plotPortfolioProduct.id === selectedId && <RiCheckLine className='text-primary-100' />}
                </TableCell>
                <TableCell className='typography-body1 p-4 align-middle font-medium'>
                  {plotPortfolioProduct.display_name}
                </TableCell>
                <TableCell className='typography-body1 p-4 align-middle'>
                  {plotPortfolioProduct.initial_setup_cost_credits}
                </TableCell>
                <TableCell className='typography-body1 p-4 align-middle'>
                  {plotPortfolioProduct.plot_creation_cost_credits}
                </TableCell>
                <TableCell className='typography-body1 p-4 align-middle'>
                  {plotPortfolioProduct.max_plots ?? t('shared.ndiProducts.noLimit')}
                </TableCell>
                <TableCell className='typography-body1 p-4 align-middle'>
                  {plotPortfolioProduct.max_hectares ?? t('shared.ndiProducts.noLimit')}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      <Pagination
        className='typography-overline text-text-secondary mx-auto'
        currentPage={currentPage}
        totalCount={data.count}
        pageSize={PRODUCT_PAGE_SIZE}
        onPageChange={setCurrentPage}
      />
    </Stack>
  );
};

const PlotPortfolioSubscriptionOverrideForm = ({
  organizationId,
  plotPortfolioProduct,
  onClose,
}: {
  organizationId: string;
  plotPortfolioProduct: PlotPortfolioProduct;
  onClose: () => void;
}) => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const postMutation = usePostPlotPortfolioSubscription();

  const form = useForm<PlotPortfolioSubscriptionFormValues>({
    defaultValues: {
      display_name: plotPortfolioProduct.display_name,
      start_date: '',
      end_date: '',
      initial_setup_cost_credits: String(plotPortfolioProduct.initial_setup_cost_credits),
      plot_creation_cost_credits: String(plotPortfolioProduct.plot_creation_cost_credits),
      max_plots:
        plotPortfolioProduct.max_plots !== null && plotPortfolioProduct.max_plots !== undefined
          ? String(plotPortfolioProduct.max_plots)
          : '',
      max_hectares:
        plotPortfolioProduct.max_hectares !== null && plotPortfolioProduct.max_hectares !== undefined
          ? String(plotPortfolioProduct.max_hectares)
          : '',
    },
  });

  const handleSubmit = form.handleSubmit(async (values) => {
    try {
      await postMutation.mutateAsync({
        bodyData: {
          org: organizationId,
          plot_portfolio_product: plotPortfolioProduct.id,
          display_name: values.display_name,
          start_date: values.start_date,
          end_date: values.end_date,
          initial_setup_cost_credits: Number(values.initial_setup_cost_credits),
          plot_creation_cost_credits: Number(values.plot_creation_cost_credits),
          max_plots: values.max_plots === '' ? null : Number(values.max_plots),
          max_hectares: values.max_hectares === '' ? null : Number(values.max_hectares),
        },
      });

      queryClient.invalidateQueries({ queryKey: [PLOT_PORTFOLIO_SUBSCRIPTIONS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [PLOT_PORTFOLIO_PRODUCTS_QUERY_KEY] });
      toast({ type: 'success', title: t('shared.ndiOrganizationSubscriptions.toast.createSuccess') });
      onClose();
    } catch {
      toast({ type: 'error', title: t('shared.ndiOrganizationSubscriptions.toast.saveError') });
    }
  });

  return (
    <Form {...form}>
      <form onSubmit={handleSubmit} className='flex flex-col gap-4'>
        <PlotPortfolioSubscriptionFormFields control={form.control} />
        <Button
          type='submit'
          className='self-end w-fit px-6'
          loading={postMutation.isPending}
          disabled={postMutation.isPending}
        >
          {t('global.ui.buttons.save')}
        </Button>
      </form>
    </Form>
  );
};
